/**
 * FMEAEditor - Failure Modes and Effects Analysis Editor
 * 
 * Structured worksheet editor for creating and editing FMEA documents per ARP4761A.
 * Features:
 * - Tabular worksheet view
 * - Failure mode definition with effects analysis
 * - Severity classification
 * - RPN calculation (optional)
 * - Traceability to reliability data and FTA events
 * 
 * COMPLIANCE: Failure modes must trace to reliability data sources.
 */

import { useState, useMemo } from 'react';
import { Button, Input, Label, Textarea, Select } from '@/components/ui';
import type {
  FMEADocument,
  FMEAItem,
  SeverityClassification,
} from '@aerotage/shared/safety';
import type { FailureRateUnit } from '@aerotage/shared/reliability';

const SEVERITY_OPTIONS: SeverityClassification[] = [
  'Catastrophic',
  'Hazardous',
  'Major',
  'Minor',
  'No_Safety_Effect',
];

interface FMEAEditorProps {
  document?: FMEADocument;
  projectId: string;
  readOnly?: boolean;
  onSave: (document: FMEADocument) => Promise<void>;
  onCancel: () => void;
}

type ActiveTab = 'worksheet' | 'summary' | 'traceability';

export function FMEAEditor({
  document,
  projectId: _projectId,
  readOnly = false,
  onSave,
  onCancel,
}: FMEAEditorProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('worksheet');
  const [items, setItems] = useState<FMEAItem[]>(document?.safetyData?.items || []);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<SeverityClassification | 'all'>('all');

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updatedDoc: FMEADocument = {
        ...document!,
        safetyData: {
          items,
          summary: generateSummary(items),
        },
      };
      await onSave(updatedDoc);
    } catch (err) {
      setError(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Generate summary
  const generateSummary = (fmeaItems: FMEAItem[]) => {
    const byClassification: Record<SeverityClassification, number> = {
      'Catastrophic': 0,
      'Hazardous': 0,
      'Major': 0,
      'Minor': 0,
      'No_Safety_Effect': 0,
    };
    
    fmeaItems.forEach(item => {
      byClassification[item.severityClassification]++;
    });

    const criticalItems = fmeaItems
      .filter(i => i.severityClassification === 'Catastrophic' || i.severityClassification === 'Hazardous')
      .slice(0, 10);

    return {
      totalFailureModes: fmeaItems.length,
      byClassification,
      topCriticalItems: criticalItems,
      recommendations: [],
      generatedAt: new Date().toISOString(),
    };
  };

  // Generate next ID
  const generateId = () => {
    const maxNum = items.reduce((max, item) => {
      const match = item.id.match(/^FMEA-(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        return num > max ? num : max;
      }
      return max;
    }, 0);
    return `FMEA-${String(maxNum + 1).padStart(3, '0')}`;
  };

  // Add new item
  const addItem = () => {
    const newItem: FMEAItem = {
      id: generateId(),
      itemNumber: '',
      itemName: '',
      function: '',
      failureMode: '',
      failureModeId: `FM-${Date.now()}`,
      localEffect: '',
      nextHigherEffect: '',
      endEffect: '',
      severityClassification: 'Minor',
      severityRationale: '',
      detectionMethod: '',
      compensatingProvisions: '',
    };
    setItems([...items, newItem]);
    setSelectedItemId(newItem.id);
  };

  // Update item
  const updateItem = (id: string, updates: Partial<FMEAItem>) => {
    setItems(items.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  // Delete item
  const deleteItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
    if (selectedItemId === id) setSelectedItemId(null);
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    if (filterSeverity === 'all') return items;
    return items.filter(i => i.severityClassification === filterSeverity);
  }, [items, filterSeverity]);

  const _selectedItem = selectedItemId ? items.find(i => i.id === selectedItemId) : null;

  // Summary stats
  const summary = useMemo(() => generateSummary(items), [items]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            FMEA Editor
          </h2>
          <p className="text-sm text-muted-foreground">
            Failure Modes and Effects Analysis - {document?.metadata?.systemName || 'System'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={readOnly || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-destructive-light border border-destructive rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {/* Stats bar */}
      <div className="flex-shrink-0 px-4 py-2 bg-muted/30 border-b border-border flex items-center gap-4 text-sm">
        <span>Total Items: <strong>{items.length}</strong></span>
        <span className="text-destructive">Catastrophic: <strong>{summary.byClassification.Catastrophic}</strong></span>
        <span className="text-warning">Hazardous: <strong>{summary.byClassification.Hazardous}</strong></span>
        <span className="text-muted-foreground">Major: <strong>{summary.byClassification.Major}</strong></span>
        <span className="text-primary">Minor: <strong>{summary.byClassification.Minor}</strong></span>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 px-4 border-b border-border">
        <div className="flex gap-4">
          {(['worksheet', 'summary', 'traceability'] as ActiveTab[]).map(tab => (
            <TabButton
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              label={tab.charAt(0).toUpperCase() + tab.slice(1)}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {activeTab === 'worksheet' && (
          <WorksheetPanel
            items={filteredItems}
            selectedId={selectedItemId}
            filterSeverity={filterSeverity}
            onFilterChange={setFilterSeverity}
            onSelect={setSelectedItemId}
            onAdd={addItem}
            onUpdate={updateItem}
            onDelete={deleteItem}
            readOnly={readOnly}
          />
        )}
        {activeTab === 'summary' && (
          <SummaryPanel summary={summary} items={items} />
        )}
        {activeTab === 'traceability' && (
          <TraceabilityPanel items={items} />
        )}
      </div>
    </div>
  );
}

// Tab button
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

function TabButton({ active, onClick, label }: TabButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={`py-2 px-3 text-sm font-medium border-b-2 rounded-none h-auto ${
        active
          ? 'border-primary text-info'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </Button>
  );
}

// Worksheet Panel
interface WorksheetPanelProps {
  items: FMEAItem[];
  selectedId: string | null;
  filterSeverity: SeverityClassification | 'all';
  onFilterChange: (filter: SeverityClassification | 'all') => void;
  onSelect: (id: string | null) => void;
  onAdd: () => void;
  onUpdate: (id: string, updates: Partial<FMEAItem>) => void;
  onDelete: (id: string) => void;
  readOnly: boolean;
}

function WorksheetPanel({
  items,
  selectedId,
  filterSeverity,
  onFilterChange,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  readOnly,
}: WorksheetPanelProps) {
  const selectedItem = selectedId ? items.find(i => i.id === selectedId) : null;

  const getSeverityColor = (severity: SeverityClassification) => {
    // Uses semantic severity tokens - auto-switches for dark mode
    switch (severity) {
      case 'Catastrophic': return 'bg-severity-catastrophic-bg text-severity-catastrophic-text';
      case 'Hazardous': return 'bg-severity-hazardous-bg text-severity-hazardous-text';
      case 'Major': return 'bg-severity-major-bg text-severity-major-text';
      case 'Minor': return 'bg-severity-minor-bg text-severity-minor-text';
      default: return 'bg-severity-none-bg text-severity-none-text';
    }
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Item list (left) */}
      <div className="w-2/5 border-r border-border overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Failure Modes</h3>
            <div className="flex gap-2">
              <Select
                value={filterSeverity}
                onChange={(e) => onFilterChange(e.target.value as SeverityClassification | 'all')}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="all">All Severities</option>
                {SEVERITY_OPTIONS.map(s => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </Select>
              {!readOnly && (
                <Button size="sm" onClick={onAdd}>+ Add</Button>
              )}
            </div>
          </div>
          
          {items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No failure modes defined</p>
              <p className="text-sm">Click "Add" to create a failure mode entry</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(item => (
                <div
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedId === item.id
                      ? 'border-primary bg-info-light'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-mono text-muted-foreground">{item.id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getSeverityColor(item.severityClassification)}`}>
                      {item.severityClassification.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="font-medium text-foreground text-sm">
                    {item.itemName || '(No item name)'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                    {item.failureMode || '(No failure mode)'}
                  </div>
                  {item.rpn && (
                    <div className="text-xs text-muted-foreground mt-1">
                      RPN: <span className={item.rpn.value > 100 ? 'text-destructive font-medium' : ''}>
                        {item.rpn.value}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Item detail (right) */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedItem ? (
          <FMEAItemForm
            item={selectedItem}
            onUpdate={(updates) => onUpdate(selectedItem.id, updates)}
            onDelete={() => onDelete(selectedItem.id)}
            readOnly={readOnly}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>Select a failure mode to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

// FMEA Item form
interface FMEAItemFormProps {
  item: FMEAItem;
  onUpdate: (updates: Partial<FMEAItem>) => void;
  onDelete: () => void;
  readOnly: boolean;
}

function FMEAItemForm({
  item,
  onUpdate,
  onDelete,
  readOnly,
}: FMEAItemFormProps) {
  // Calculate RPN if all values are set
  const _calculateRPN = () => {
    const s = item.rpn?.severity || 1;
    const o = item.rpn?.occurrence || 1;
    const d = item.rpn?.detection || 1;
    return s * o * d;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{item.id}</h3>
        {!readOnly && (
          <Button variant="secondary" size="sm" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>

      {/* Item identification */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Item Number</Label>
          <Input
            value={item.itemNumber}
            onChange={(e) => onUpdate({ itemNumber: e.target.value })}
            placeholder="e.g., 1.2.3"
            disabled={readOnly}
          />
        </div>
        <div>
          <Label>Item Name *</Label>
          <Input
            value={item.itemName}
            onChange={(e) => onUpdate({ itemName: e.target.value })}
            placeholder="e.g., AHRS Sensor Unit"
            disabled={readOnly}
          />
        </div>
      </div>

      <div>
        <Label>Function</Label>
        <Input
          value={item.function}
          onChange={(e) => onUpdate({ function: e.target.value })}
          placeholder="e.g., Provide attitude reference data"
          disabled={readOnly}
        />
      </div>

      {/* Failure mode */}
      <div className="p-4 border border-border rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Failure Mode</h4>
        <div>
          <Label>Failure Mode Description *</Label>
          <Textarea
            value={item.failureMode}
            onChange={(e) => onUpdate({ failureMode: e.target.value })}
            placeholder="e.g., Erroneous attitude output, Fails to provide data, Stuck at last value"
            rows={2}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Effects */}
      <div className="p-4 border border-border rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Effects Analysis</h4>
        <div className="space-y-3">
          <div>
            <Label>Local Effect</Label>
            <Input
              value={item.localEffect}
              onChange={(e) => onUpdate({ localEffect: e.target.value })}
              placeholder="Effect on the item itself"
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>Next Higher Level Effect</Label>
            <Input
              value={item.nextHigherEffect}
              onChange={(e) => onUpdate({ nextHigherEffect: e.target.value })}
              placeholder="Effect on the subsystem"
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>End Effect</Label>
            <Textarea
              value={item.endEffect}
              onChange={(e) => onUpdate({ endEffect: e.target.value })}
              placeholder="Effect on the aircraft and crew"
              rows={2}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {/* Severity */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Severity Classification *</Label>
          <Select
            value={item.severityClassification}
            onChange={(e) => onUpdate({ severityClassification: e.target.value as SeverityClassification })}
            disabled={readOnly}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {SEVERITY_OPTIONS.map(s => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Severity Rationale</Label>
          <Input
            value={item.severityRationale}
            onChange={(e) => onUpdate({ severityRationale: e.target.value })}
            placeholder="Basis for classification"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Failure rate data */}
      <div className="p-4 border border-border rounded-lg bg-muted/30">
        <h4 className="font-medium text-foreground mb-3">Failure Rate Data</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Failure Rate (λ)</Label>
            <Input
              type="number"
              value={item.failureRate || ''}
              onChange={(e) => onUpdate({ failureRate: parseFloat(e.target.value) || undefined })}
              placeholder="e.g., 1.5e-6"
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>Unit</Label>
            <Select
              value={item.failureRateUnit || 'per_hour'}
              onChange={(e) => onUpdate({ failureRateUnit: e.target.value as FailureRateUnit })}
              disabled={readOnly}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="per_hour">per hour</option>
              <option value="per_million_hours">per million hours</option>
              <option value="FIT">FIT</option>
            </Select>
          </div>
          <div>
            <Label>Mode Probability (α)</Label>
            <Input
              type="number"
              value={item.failureModeProbability || ''}
              onChange={(e) => onUpdate({ failureModeProbability: parseFloat(e.target.value) || undefined })}
              placeholder="e.g., 0.3"
              disabled={readOnly}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          ⚠️ Failure rates MUST trace to approved reliability data sources for certification.
        </p>
      </div>

      {/* Detection and mitigation */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label>Detection Method</Label>
          <Input
            value={item.detectionMethod}
            onChange={(e) => onUpdate({ detectionMethod: e.target.value })}
            placeholder="e.g., BITE, Crew annunciation, Comparison monitor"
            disabled={readOnly}
          />
        </div>
        <div>
          <Label>Compensating Provisions</Label>
          <Textarea
            value={item.compensatingProvisions}
            onChange={(e) => onUpdate({ compensatingProvisions: e.target.value })}
            placeholder="Provisions that mitigate or compensate for this failure mode"
            rows={2}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* RPN (optional) */}
      <div className="p-4 border border-border rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Risk Priority Number (Optional)</h4>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label>Severity (1-10)</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={item.rpn?.severity || ''}
              onChange={(e) => {
                const severity = parseInt(e.target.value) || undefined;
                onUpdate({ 
                  rpn: severity ? { 
                    ...item.rpn, 
                    severity,
                    value: (severity * (item.rpn?.occurrence || 1) * (item.rpn?.detection || 1))
                  } : undefined 
                });
              }}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>Occurrence (1-10)</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={item.rpn?.occurrence || ''}
              onChange={(e) => {
                const occurrence = parseInt(e.target.value) || undefined;
                onUpdate({ 
                  rpn: occurrence ? { 
                    ...item.rpn, 
                    occurrence,
                    value: ((item.rpn?.severity || 1) * occurrence * (item.rpn?.detection || 1))
                  } : undefined 
                });
              }}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>Detection (1-10)</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={item.rpn?.detection || ''}
              onChange={(e) => {
                const detection = parseInt(e.target.value) || undefined;
                onUpdate({ 
                  rpn: detection ? { 
                    ...item.rpn, 
                    detection,
                    value: ((item.rpn?.severity || 1) * (item.rpn?.occurrence || 1) * detection)
                  } : undefined 
                });
              }}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>RPN Value</Label>
            <Input
              value={item.rpn?.value || '-'}
              disabled
              className="bg-muted font-mono"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          RPN = Severity × Occurrence × Detection. Values &gt;100 may require action.
        </p>
      </div>
    </div>
  );
}

// Summary Panel
interface FMEASummary {
  totalFailureModes: number;
  byClassification: Record<SeverityClassification, number>;
  topCriticalItems: FMEAItem[];
  recommendations: string[];
  generatedAt: string;
}

function SummaryPanel({ summary, items: _items }: { summary: FMEASummary; items: FMEAItem[] }) {
  const getSeverityColor = (severity: SeverityClassification) => {
    switch (severity) {
      case 'Catastrophic': return 'bg-red-500';
      case 'Hazardous': return 'bg-orange-500';
      case 'Major': return 'bg-yellow-500';
      case 'Minor': return 'bg-blue-500';
      default: return 'bg-green-500';
    }
  };

  return (
    <div className="flex-1 overflow-auto p-4">
      <h3 className="font-semibold text-foreground mb-4">FMEA Summary (FMES)</h3>
      
      {/* Classification breakdown */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {SEVERITY_OPTIONS.map(severity => (
          <div key={severity} className="p-4 border border-border rounded-lg text-center">
            <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${getSeverityColor(severity)}`} />
            <div className="text-2xl font-bold text-foreground">
              {summary.byClassification[severity]}
            </div>
            <div className="text-xs text-muted-foreground">
              {severity.replace('_', ' ')}
            </div>
          </div>
        ))}
      </div>
      
      {/* Critical items */}
      <div className="mb-6">
        <h4 className="font-medium text-foreground mb-2">Critical Items (Catastrophic/Hazardous)</h4>
        {summary.topCriticalItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No critical items identified.</p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 text-left font-medium">ID</th>
                  <th className="p-2 text-left font-medium">Item</th>
                  <th className="p-2 text-left font-medium">Failure Mode</th>
                  <th className="p-2 text-left font-medium">Severity</th>
                </tr>
              </thead>
              <tbody>
                {summary.topCriticalItems.map((item: FMEAItem) => (
                  <tr key={item.id} className="border-t border-border">
                    <td className="p-2 font-mono text-xs">{item.id}</td>
                    <td className="p-2">{item.itemName}</td>
                    <td className="p-2 text-muted-foreground">{item.failureMode}</td>
                    <td className="p-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        item.severityClassification === 'Catastrophic' 
                          ? 'bg-severity-catastrophic-bg text-severity-catastrophic-text' 
                          : 'bg-severity-hazardous-bg text-severity-hazardous-text'
                      }`}>
                        {item.severityClassification}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Stats */}
      <div className="text-sm text-muted-foreground">
        <p>Total failure modes analyzed: {summary.totalFailureModes}</p>
        <p>Summary generated: {new Date(summary.generatedAt).toLocaleString()}</p>
      </div>
    </div>
  );
}

// Traceability Panel
interface TraceabilityPanelProps {
  items: FMEAItem[];
}

function TraceabilityPanel({ items }: TraceabilityPanelProps) {
  const linkedItems = items.filter(i => i.reliabilityLink || i.failureRate);
  const unlinkedItems = items.filter(i => !i.reliabilityLink && !i.failureRate);

  return (
    <div className="flex-1 overflow-auto p-4">
      <h3 className="font-semibold text-foreground mb-4">Reliability Data Traceability</h3>
      
      <div className="mb-6 p-4 bg-info-light border border-info rounded-lg">
        <p className="text-sm text-info-foreground">
          <strong>Compliance Requirement:</strong> All failure rates used in FMEA must trace back 
          to approved, versioned reliability data sources (MIL-HDBK-217, vendor data, etc.) 
          for certification compliance.
        </p>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 border border-success bg-success-light rounded-lg">
          <div className="text-2xl font-bold text-success">{linkedItems.length}</div>
          <div className="text-sm text-muted-foreground">Items with failure rate data</div>
        </div>
        <div className={`p-4 border rounded-lg ${
          unlinkedItems.length > 0 
            ? 'border-warning bg-warning-light' 
            : 'border-success bg-success-light'
        }`}>
          <div className={`text-2xl font-bold ${unlinkedItems.length > 0 ? 'text-warning' : 'text-success'}`}>
            {unlinkedItems.length}
          </div>
          <div className="text-sm text-muted-foreground">Items missing failure rate data</div>
        </div>
      </div>
      
      {/* Unlinked items warning */}
      {unlinkedItems.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-2">⚠️ Items Requiring Reliability Data</h4>
          <div className="space-y-2">
            {unlinkedItems.map(item => (
              <div key={item.id} className="p-3 border border-warning bg-warning-light rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{item.id}</span>
                  <span className="font-medium">{item.itemName}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Failure Mode: {item.failureMode || '(not specified)'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Linked items */}
      {linkedItems.length > 0 && (
        <div>
          <h4 className="font-medium text-foreground mb-2">✓ Items with Reliability Data</h4>
          <div className="space-y-2">
            {linkedItems.map(item => (
              <div key={item.id} className="p-3 border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{item.id}</span>
                    <span className="font-medium">{item.itemName}</span>
                  </div>
                  {item.failureRate && (
                    <span className="font-mono text-xs text-muted-foreground">
                      λ = {item.failureRate.toExponential(2)}/{item.failureRateUnit || 'hr'}
                    </span>
                  )}
                </div>
                {item.reliabilityLink && (
                  <div className="text-xs text-success mt-1">
                    ✓ Linked to reliability data source
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FMEAEditor;
